import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  TicketIcon, ChevronLeft, Send, AlertCircle,
  AlertTriangle, ArrowUp, Minus, ArrowDown, Paperclip,
  CheckCircle2, Clock, ArrowRight, ListChecks,
} from "lucide-react";
import {
  useGetTicketCategoriesQuery,
  useCreateTicketMutation,
  TicketPriority,
  Ticket,
} from "../api/supportApi";
import FileUploader from "../components/FileUploader";

const PRIORITIES: {
  value: TicketPriority;
  label: string;
  desc: string;
  icon: React.ElementType;
  active: string;
  inactive: string;
}[] = [
  {
    value: "low",
    label: "Low",
    desc: "Minor, not time-sensitive",
    icon: ArrowDown,
    active: "border-gray-500 bg-gray-50 text-gray-700",
    inactive: "border-gray-200 text-gray-500 hover:border-gray-400",
  },
  {
    value: "medium",
    label: "Medium",
    desc: "Affects work, has workaround",
    icon: Minus,
    active: "border-blue-500 bg-blue-50 text-blue-700",
    inactive: "border-gray-200 text-gray-500 hover:border-blue-300",
  },
  {
    value: "high",
    label: "High",
    desc: "Significantly disrupts work",
    icon: ArrowUp,
    active: "border-orange-500 bg-orange-50 text-orange-700",
    inactive: "border-gray-200 text-gray-500 hover:border-orange-300",
  },
  {
    value: "urgent",
    label: "Urgent",
    desc: "Critical — needs immediate attention",
    icon: AlertTriangle,
    active: "border-red-500 bg-red-50 text-red-700",
    inactive: "border-gray-200 text-gray-500 hover:border-red-300",
  },
];

function SuccessScreen({ ticket }: { ticket: Ticket }) {
  return (
    <div className="space-y-5 max-w-2xl mx-auto p-4 md:p-6 pb-20">
      {/* Success card */}
      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm text-center space-y-4">
        <div className="w-16 h-16 rounded-full bg-emerald-50 border-2 border-emerald-100 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-8 h-8 text-emerald-500" />
        </div>
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none">Ticket Submitted!</h1>
          <p className="text-gray-400 text-sm mt-2">Your support request has been received. We'll get back to you as soon as possible.</p>
        </div>
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl">
          <TicketIcon className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-xs font-black text-gray-500 uppercase tracking-widest">{ticket.reference}</span>
        </div>
      </div>

      {/* Auto-close notice */}
      <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5 flex items-start gap-3">
        <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
          <Clock className="w-4 h-4 text-amber-600" />
        </div>
        <div>
          <p className="text-sm font-black text-amber-800">Please respond within 3 days</p>
          <p className="text-xs text-amber-700 mt-1 leading-relaxed">
            Once a support agent replies to your ticket, you have <span className="font-bold">3 days</span> to respond.
            Tickets with no member activity after 3 days of an admin reply will be automatically closed.
            You can always reopen a new ticket if your issue persists.
          </p>
        </div>
      </div>

      {/* What's next */}
      <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm space-y-3">
        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">What happens next</p>
        <div className="space-y-2.5">
          {[
            "Your ticket has been assigned a reference number above",
            "A support agent will review and respond — typically within 24 hours",
            "You'll receive an email notification when there's a reply",
            "Log in and reply to keep the conversation going",
          ].map((step, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-black flex items-center justify-center shrink-0 mt-0.5">
                {i + 1}
              </div>
              <p className="text-sm text-gray-600">{step}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Link
          to={`/support/tickets/${ticket.id}`}
          className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-gray-900 text-white text-sm font-black rounded-2xl hover:bg-gray-800 transition-all shadow-xl shadow-gray-900/20 active:scale-[0.98]"
        >
          <TicketIcon className="w-4 h-4" />
          View My Ticket
          <ArrowRight className="w-4 h-4" />
        </Link>
        <Link
          to="/support/tickets"
          className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-white border border-gray-200 text-gray-700 text-sm font-black rounded-2xl hover:bg-gray-50 transition-all shadow-sm active:scale-[0.98]"
        >
          <ListChecks className="w-4 h-4" />
          All My Tickets
        </Link>
      </div>
    </div>
  );
}

export default function CreateTicketPage() {
  const navigate = useNavigate();
  const { data: categories = [] } = useGetTicketCategoriesQuery();
  const [createTicket, { isLoading, error }] = useCreateTicketMutation();

  const [form, setForm] = useState({
    subject:     "",
    category_id: "" as string | number,
    priority:    "medium" as TicketPriority,
    description: "",
  });
  const [files, setFiles] = useState<File[]>([]);
  const [createdTicket, setCreatedTicket] = useState<Ticket | null>(null);

  const set = (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((p) => ({ ...p, [k]: e.target.value }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      const ticket = await createTicket({
        subject:     form.subject,
        description: form.description,
        priority:    form.priority,
        category_id: form.category_id ? Number(form.category_id) : undefined,
        attachments: files.length ? files : undefined,
      }).unwrap();
      setCreatedTicket(ticket);
    } catch {}
  }

  if (createdTicket) {
    return <SuccessScreen ticket={createdTicket} />;
  }

  const errMsg    = (error as any)?.data?.message ?? (error as any)?.error;
  const charCount = form.description.length;

  return (
    <div className="space-y-5 max-w-2xl mx-auto p-4 md:p-6 pb-20">

      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-600 text-sm font-bold rounded-xl hover:bg-gray-50 transition-all shadow-sm active:scale-95"
      >
        <ChevronLeft className="w-4 h-4" />
        Back
      </button>

      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-900/20 shrink-0">
            <TicketIcon className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">New Support Ticket</h1>
            <p className="text-gray-400 text-sm mt-1.5">Describe your issue and we'll get back to you as soon as possible.</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm space-y-6">

        {errMsg && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-2xl text-sm text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {errMsg}
          </div>
        )}

        {/* Subject */}
        <div className="space-y-1.5">
          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
            Subject <span className="text-red-500">*</span>
          </label>
          <input
            value={form.subject}
            onChange={set("subject")}
            required
            maxLength={180}
            placeholder="Brief description of your issue"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition"
          />
        </div>

        {/* Category */}
        <div className="space-y-1.5">
          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Category</label>
          <select
            value={form.category_id}
            onChange={set("category_id")}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 transition bg-white"
          >
            <option value="">— Select a category —</option>
            {categories.filter((c) => c.is_active).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        {/* Priority Cards */}
        <div className="space-y-2">
          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
            Priority <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            {PRIORITIES.map((p) => {
              const Icon = p.icon;
              const isActive = form.priority === p.value;
              return (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, priority: p.value }))}
                  className={`flex flex-col items-start gap-1.5 p-3.5 rounded-2xl border-2 transition-all text-left ${
                    isActive ? p.active : `bg-white ${p.inactive}`
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-xs font-black leading-none">{p.label}</span>
                  <span className={`text-[9px] font-medium leading-tight ${isActive ? "opacity-75" : "text-gray-400"}`}>
                    {p.desc}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
              Description <span className="text-red-500">*</span>
            </label>
            <span className={`text-[10px] font-bold ${charCount > 1800 ? "text-red-400" : "text-gray-300"}`}>
              {charCount}/2000
            </span>
          </div>
          <textarea
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value.slice(0, 2000) }))}
            required
            rows={6}
            placeholder="Describe your issue in detail — include what you expected, what actually happened, and steps to reproduce."
            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 resize-none transition"
          />
        </div>

        {/* Attachments */}
        <div className="space-y-1.5">
          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
            <Paperclip className="w-3 h-3" />
            Attachments
            <span className="font-normal normal-case tracking-normal text-gray-400">(optional · up to 5 files)</span>
          </label>
          <FileUploader files={files} onChange={setFiles} maxFiles={5} />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 py-3.5 bg-gray-900 text-white text-sm font-black rounded-2xl hover:bg-gray-800 transition-all shadow-xl shadow-gray-900/20 disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.98]"
        >
          <Send className="w-4 h-4" />
          {isLoading ? "Submitting…" : "Submit Ticket"}
        </button>
      </form>
    </div>
  );
}
